// सर्वनाम: the ten pronoun tables of the engine (PRONOUNS / PRONOUN_META), seven cases × two numbers (no सम्बोधन),
// on the "१. सामान्य · २. विकल्प" tabs of the tense pages for Kids and Adults alike: core forms, or core + variant
// forms (हं, अम्मि, वयं, तुं, तुह, तुब्भे, तुज्झे, ताउ, इदं, इमिणा). Tap a cell for its role, form and the Word Explorer.
// Also the प्रथमा summary of docs/03 §3, which follows the chosen tab. ?table=amha opens a table.

import type { FC, ReactNode } from 'react'
import type { Gender, Num } from '@/prakrit/lib/grammar/types'
import type { PronounMeta } from '@/prakrit/lib/grammar/pronouns'
import type { KalMode } from '@/prakrit/components/grammar/kal/kal-tabs'
import type { SelectSource } from '@/prakrit/components/grammar/shabd/declension-table'

import { useEffect, useMemo, useRef, useState } from 'react'
import { Info, Users } from 'lucide-react'
import { cn } from '@/lib/utils'
import { caseCells } from '@/prakrit/lib/grammar/cells'
import { PRONOUNS, PRONOUN_META, pronounMeta } from '@/prakrit/lib/grammar/pronouns'
import { NUMBERS, PRONOUN_CASES } from '@/prakrit/lib/grammar/types'
import { useLemmaStatuses } from '@/hooks/use-statuses'
import { useReducedMotion } from '@/hooks/use-media-query'
import { Bi } from '@/prakrit/components/common/bi'
import { PrakritWord } from '@/prakrit/components/common/prakrit-word'
import { StatusBadge } from '@/prakrit/components/common/status-badge'
import { KalTabs } from '@/prakrit/components/grammar/kal/kal-tabs'
import { CaseCellPanel } from '@/prakrit/components/grammar/labs/case-cell-panel'
import { TwinBracket, TwinNote } from '@/prakrit/components/grammar/labs/case-twin'
import { useUrlQuery } from '@/prakrit/components/grammar/labs/use-url-query'
import { CASE_TWIN } from '@/prakrit/components/grammar/labs/noun-data'
import { DeclensionTable, VikalpNote, cellsHaveAlt } from '@/prakrit/components/grammar/shabd/declension-table'

import {
    CASE_LABEL,
    GENDER_LABEL,
    GENDER_SHORT,
    NUMBER_LABEL,
    PERSON_LABEL
} from '@/prakrit/lib/grammar/labels'
import {
    CARD,
    CONTAINER,
    LabHeader,
    LabNav,
    Notice,
    SectionTitle
} from '@/prakrit/components/grammar/labs/lab-ui'

const DEFAULT_SLUG = 'amha'

type PronounCase = typeof PRONOUN_CASES[number]

/** The five pronoun families; the Prakrit base of each comes from PRONOUN_META. */
const FAMILY_LABEL: Record<string, { hi: string, en: string }> = {
    'अम्ह': { hi: 'मैं, हम', en: 'I, we' },
    'तुम्ह': { hi: 'तुम, तुम सब', en: 'you, you all' },
    'त': { hi: 'वह, वे', en: 'he, she, it, they' },
    'इम': { hi: 'यह, ये', en: 'this, these' },
    'क': { hi: 'कौन', en: 'who' }
}

const familyOf = (m: PronounMeta): string => m.base.replace(/ा$/, '')

const FAMILIES: { base: string, members: PronounMeta[] }[] = PRONOUN_META.reduce<{ base: string, members: PronounMeta[] }[]>((out, m) => {
    const base = familyOf(m)
    const family = out.find(f => f.base === base)

    if (family) family.members.push(m)
    else out.push({ base, members: [m] })

    return out
}, [])

const coreForms = (key: string, c: PronounCase, n: Num): string[] => (
    PRONOUNS[key]?.[c]?.[n]?.filter(f => f.status === 'core').map(f => f.form) ?? []
)

const altForms = (key: string, c: PronounCase, n: Num): string[] => (
    PRONOUNS[key]?.[c]?.[n]?.filter(f => f.status === 'alt').map(f => f.form) ?? []
)

/** Cases in which a table's core forms differ from another table's (e.g. नपुंसक vs पुल्लिंग). */
const differingCases = (key: string, other: string): PronounCase[] => PRONOUN_CASES.filter(c => (
    NUMBERS.some(n => coreForms(key, c, n).join('|') !== coreForms(other, c, n).join('|'))
))

/** प्रथमा summary (docs/03 §3): rows by person and gender, forms straight from the engine. */
const SUMMARY_ROWS: { hi: string, en: string, metas: PronounMeta[] }[] = [
    { hi: PERSON_LABEL.uttam.hi, en: 'speaker', metas: PRONOUN_META.filter(m => m.person === 'uttam') },
    { hi: PERSON_LABEL.madhyam.hi, en: 'addressee', metas: PRONOUN_META.filter(m => m.person === 'madhyam') },
    ...(['pu', 'stri', 'napum'] as Gender[]).map(g => ({
        hi: `अन्य ${GENDER_SHORT[g]}`,
        en: `the other, ${GENDER_LABEL[g].en}`,
        metas: PRONOUN_META.filter(m => m.person === 'anya' && m.gender === g)
    }))
]

/** On the विकल्प tab the summary lists each pronoun's variant subject forms too (in terracotta, marked विकल्प). */
const PrathamaSummary: FC<{ mode: KalMode }> = ({ mode }): ReactNode => (
    <section aria-labelledby='summary-title' className={cn(CARD, 'flex flex-col gap-4')}>
        <SectionTitle id='summary-title' hi='प्रथमा: एक नज़र में' en='The nominative at a glance' level='h3'>
            <p className='text-sm text-muted-foreground'>
                <Bi
                    hi='हर सर्वनाम का प्रथमा रूप — वाक्य का कर्ता। किसी भी शब्द पर टैप करें।'
                    en='The प्रथमा form of every pronoun, the one used as the subject. Tap any word.'
                />
            </p>
        </SectionTitle>
        <div className='overflow-x-auto'>
            <table className='w-full min-w-[22rem] border-collapse text-left'>
                <caption className='sr-only'>प्रथमा विभक्ति के सर्वनाम रूप · Nominative pronoun forms</caption>
                <thead>
                    <tr className='border-b border-border bg-surface-muted/70'>
                        <th scope='col' className='px-3 py-2 text-xs font-semibold'>
                            <span lang='hi' className='hindi'>पुरुष / लिङ्ग</span>
                        </th>
                        {NUMBERS.map(n => (
                            <th key={n} scope='col' className='px-3 py-2 text-xs font-semibold'>
                                <span lang='hi' className='hindi'>{NUMBER_LABEL[n].hi}</span>
                                <span lang='en' className='ml-1.5 font-normal text-muted-foreground'>{NUMBER_LABEL[n].en}</span>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {SUMMARY_ROWS.map(row => (
                        <tr key={row.hi} className='border-b border-border last:border-b-0'>
                            <th scope='row' className='px-3 py-2.5 align-top font-normal'>
                                <span lang='hi' className='hindi block text-sm font-semibold'>{row.hi}</span>
                                <span lang='en' className='block text-xs text-muted-foreground'>{row.en}</span>
                            </th>
                            {NUMBERS.map(n => (
                                <td key={n} className='px-3 py-2.5 align-top'>
                                    <span lang='pra' className='pra pra-md flex flex-wrap items-baseline gap-x-2'>
                                        {row.metas.map((m, i) => {
                                            const alt = mode === 'vikalp' ? altForms(m.key, 'prathama', n) : []

                                            return (
                                                <span key={m.key} className='inline-flex flex-wrap items-baseline gap-x-2'>
                                                    {i > 0 && <span aria-hidden='true' className='font-sans text-base font-normal text-muted-foreground'>·</span>}
                                                    {coreForms(m.key, 'prathama', n).map(f => <PrakritWord key={f} word={f} />)}
                                                    {alt.length > 0 && (
                                                        <span className='inline-flex flex-wrap items-center gap-x-2'>
                                                            {alt.map(f => <PrakritWord key={f} word={f} className='text-terracotta' />)}
                                                            <StatusBadge kind='alt' withEnglish={false} />
                                                        </span>
                                                    )}
                                                </span>
                                            )
                                        })}
                                    </span>
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
        <p className='text-xs text-muted-foreground'>
            <Bi
                hi='अन्य पुरुष की पंक्तियों में क्रम: वह (त / ता) · यह (इम / इमा) · कौन (क / का)।'
                en='Order in the अन्य rows: he/she/it (त / ता) · this (इम / इमा) · who (क / का).'
            />
        </p>
        {mode === 'vikalp' && (
            <p className='text-xs text-muted-foreground'>
                <span lang='hi' data-ex='hi' className='hindi'>ऊपर तालिका का “२. विकल्प” टैब चुना है, इसलिए यहाँ विकल्प रूप भी हैं।</span>
                <span aria-hidden='true' className='ex-sep'> · </span>
                <span lang='en' data-ex='en'>The table’s “Variants” tab is chosen above, so the variant forms are listed here too.</span>
            </p>
        )}
    </section>
)

export const SarvanamLab: FC = (): ReactNode => {
    const reduced = useReducedMotion()
    const url = useUrlQuery()
    const [slug, setSlug] = useState(DEFAULT_SLUG)
    const [selected, setSelected] = useState<{ case: PronounCase, number: Num }>({ case: 'prathama', number: 'eka' })
    const [mode, setMode] = useState<KalMode>('samanya')
    const gridRef = useRef<HTMLDivElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)

    // The URL chooses the table (?table=ta-pu), also when a link on this page changes it.
    const urlTable = url.read('table')

    useEffect(() => {
        if (!url.ready) return

        setSlug((urlTable && pronounMeta(urlTable)?.slug) || DEFAULT_SLUG)
    }, [url.ready, urlTable])

    const meta = pronounMeta(slug) ?? PRONOUN_META[0]
    const table = PRONOUNS[meta.key]
    const showAlt = mode === 'vikalp'
    const status = useLemmaStatuses('pronoun_form', meta.key)
    const cells = useMemo(() => caseCells('pf', meta.key, table ?? {}, status), [meta.key, table, status])
    const cases: PronounCase[] = [...PRONOUN_CASES]
    const selectedCell = cells[selected.case]?.[selected.number]
    const twinCase = CASE_TWIN[selected.case] as PronounCase | undefined
    const highlight = twinCase ? [{ case: twinCase, number: selected.number }] : []
    const twinRows: [number, number] = [cases.indexOf('chaturthi'), cases.indexOf('shashthi')]
    const hasAlt = cellsHaveAlt(cells)

    // नपुंसक tables: which cases differ from the पुल्लिंग table of the same pronoun.
    const masculine = meta.gender === 'napum' ? PRONOUN_META.find(m => familyOf(m) === familyOf(meta) && m.gender === 'pu') : undefined
    const differs = masculine ? differingCases(meta.key, masculine.key) : []

    const selectTable = (m: PronounMeta) => {
        setSlug(m.slug)
        url.write({ table: m.slug })
    }

    const select = (c: PronounCase, n: Num) => setSelected({ case: c, number: n })

    // On narrow screens the panel sits under the table: bring it into view on a tap, not while the arrow keys move.
    const selectTableCell = (c: PronounCase, n: Num, source: SelectSource) => {
        select(c, n)

        const panel = panelRef.current

        if (!panel || source === 'arrow' || window.innerWidth >= 1280) return

        if (panel.getBoundingClientRect().top > window.innerHeight - 140) {
            panel.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' })
        }
    }

    return (
        <div className={CONTAINER}>
            <LabHeader
                eyebrow='सर्वनाम · ७ × २'
                title={{ hi: 'सर्वनाम तालिकाएँ', en: 'Pronoun tables' }}
                intro={{
                    hi: 'मैं, तुम, वह, यह, कौन — दस तालिकाएँ, सात विभक्तियाँ और दो वचन। सर्वनाम का सम्बोधन रूप नहीं होता, इसलिए यहाँ सात ही पंक्तियाँ हैं।',
                    en: 'I, you, he, she, it, this, who: ten tables, seven cases and two numbers. Pronouns have no vocative, so there are seven rows.'
                }}
                kidsIntro={{
                    hi: 'मैं, तुम, वह, यह, कौन — प्राकृत में ये शब्द कैसे बदलते हैं, देखो!',
                    en: 'I, you, he, this, who: see how these words change in Prakrit!'
                }}
                source='नि.१–७, २७, ३१, ३६, ४२, ४६, ५१'
            />

            <section aria-labelledby='pronoun-picker' className={cn(CARD, 'flex flex-col gap-4')}>
                <h2 id='pronoun-picker' className='text-base font-semibold'>
                    <span lang='hi' className='hindi'>सर्वनाम चुनें</span>
                    <span aria-hidden='true' className='text-muted-foreground'> · </span>
                    <span lang='en' className='font-sans font-medium text-muted-foreground'>Choose a pronoun</span>
                </h2>
                <ul className='grid gap-3 sm:grid-cols-2 lg:grid-cols-5'>
                    {FAMILIES.map(f => {
                        const label = FAMILY_LABEL[f.base]
                        const inFamily = f.members.some(m => m.key === meta.key)

                        return (
                            <li
                                key={f.base}
                                className={cn(
                                    'flex flex-col gap-2 rounded-xl border px-3 py-3',
                                    inFamily ? 'border-prakrit/35 bg-prakrit/5' : 'border-border bg-surface-muted/40'
                                )}
                            >
                                <span className='flex flex-wrap items-baseline gap-x-2'>
                                    <span lang='pra' className='pra pra-md'>{f.base}</span>
                                    {label && <Bi hi={label.hi} en={label.en} layout='inline' className='text-sm' enClassName='text-xs text-muted-foreground' />}
                                </span>
                                <div role='group' aria-label={`${f.base} · ${label?.en ?? ''}`} className='flex flex-wrap gap-1.5'>
                                    {f.members.map(m => {
                                        const active = m.key === meta.key
                                        const nominative = coreForms(m.key, 'prathama', 'eka')[0]

                                        return (
                                            <button
                                                key={m.key}
                                                type='button'
                                                aria-pressed={active}
                                                onClick={() => selectTable(m)}
                                                className={cn(
                                                    'inline-flex min-h-tap items-center gap-2 rounded-lg border px-3 py-1 text-left transition-colors',
                                                    active ? 'border-prakrit/45 bg-card shadow-sm ring-2 ring-prakrit/20' : 'border-border bg-card hover:border-saffron/60'
                                                )}
                                            >
                                                <span lang='pra' className='font-prakrit text-lg font-semibold text-prakrit'>{nominative}</span>
                                                <span lang='hi' className='hindi text-xs text-muted-foreground'>
                                                    {m.gender ? GENDER_SHORT[m.gender] : 'सभी लिङ्ग'}
                                                </span>
                                                <span lang='en' className='sr-only'>{m.en}</span>
                                            </button>
                                        )
                                    })}
                                </div>
                            </li>
                        )
                    })}
                </ul>
            </section>

            <div className='mt-6 grid gap-6 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] xl:items-start'>
                <section aria-labelledby='pronoun-title' className={cn(CARD, 'flex min-w-0 flex-col gap-4 p-4 sm:p-5')}>
                    <div className='flex flex-col gap-1'>
                        <h3 id='pronoun-title' className='flex flex-wrap items-baseline gap-x-2'>
                            <span lang='pra' className='pra pra-xl'>{meta.base}</span>
                            <span lang='hi' className='text-lg font-bold'>{meta.hi}</span>
                        </h3>
                        <p className='flex flex-wrap gap-2 text-xs'>
                            <span className='rounded-full border border-border bg-secondary px-2.5 py-1'>
                                <span lang='hi' className='hindi font-medium'>{PERSON_LABEL[meta.person].hi}</span>
                            </span>
                            <span className='rounded-full border border-border bg-secondary px-2.5 py-1'>
                                <span lang='hi' className='hindi font-medium'>{meta.gender ? GENDER_LABEL[meta.gender].hi : 'तीनों लिङ्ग'}</span>
                                <span aria-hidden='true' className='text-muted-foreground'> · </span>
                                <span lang='en' className='text-muted-foreground'>{meta.gender ? GENDER_LABEL[meta.gender].en : 'all genders'}</span>
                            </span>
                            <span lang='en' className='rounded-full border border-border bg-card px-2.5 py-1 text-muted-foreground'>{meta.en}</span>
                        </p>
                    </div>

                    {meta.gender === null && (
                        <Notice tone='link' icon={<Users className='size-4 text-terracotta' />}>
                            <Bi
                                hi={`${meta.base} के रूप पुल्लिंग, स्त्रीलिंग और नपुंसकलिंग — तीनों में एक जैसे हैं। ${meta.person === 'uttam' ? 'बोलने वाला' : 'सुनने वाला'} कोई भी हो, रूप वही रहता है।`}
                                en={`The forms of ${meta.base} are the same for masculine, feminine and neuter: whoever is ${meta.person === 'uttam' ? 'speaking' : 'spoken to'}, the form stays the same.`}
                                className='gap-0.5'
                            />
                        </Notice>
                    )}

                    {masculine && differs.length > 0 && (
                        <Notice tone='info' icon={<Info className='size-4 text-saffron-strong' />}>
                            <Bi
                                hi={`नपुंसकलिंग में केवल ${differs.map(c => CASE_LABEL[c].hi).join(' और ')} के रूप अलग हैं; बाकी सब रूप पुल्लिंग ${masculine.base} जैसे ही हैं।`}
                                en={`In the neuter only the ${differs.map(c => CASE_LABEL[c].en).join(' and ')} forms differ; every other form is the same as masculine ${masculine.base}.`}
                                className='gap-0.5'
                            />
                        </Notice>
                    )}

                    <KalTabs id='sarvanam-roop-tabs' value={mode} onChange={setMode} label={`${meta.base}: रूप · forms`} className='gap-4'>
                        <div className='flex flex-col gap-3'>
                            <div ref={gridRef} className='relative pr-6 sm:pr-9'>
                                <DeclensionTable
                                    cells={cells}
                                    cases={cases}
                                    mode={mode}
                                    word={meta.base}
                                    gloss={`${meta.hi}, ${meta.en}`}
                                    selected={selected}
                                    highlight={highlight}
                                    showReview
                                    onSelect={(c, n, source) => selectTableCell(c as PronounCase, n, source)}
                                />
                                <TwinBracket containerRef={gridRef} rows={twinRows} layoutKey={`${meta.key}|${mode}`} />
                            </div>
                            {mode === 'vikalp' && <VikalpNote hasAlt={hasAlt} of='pronoun' />}
                        </div>
                    </KalTabs>

                    <p className='text-xs text-muted-foreground'>
                        <Bi
                            hi='किसी भी खाने पर टैप करें। कीबोर्ड: तीर-कुंजियों से खानों में घूमें; घूमते ही खाना चुना जाता है।'
                            en='Tap any cell. Keyboard: the arrow keys move between cells and choose them as you go.'
                        />
                    </p>
                </section>

                <div ref={panelRef} className='scroll-mt-24'>
                    {selectedCell && (
                        <CaseCellPanel
                            key={`${meta.key}|${selected.case}|${selected.number}`}
                            caseName={selected.case}
                            number={selected.number}
                            cell={selectedCell}
                            entries={table?.[selected.case]?.[selected.number] ?? []}
                            showAlt={showAlt}
                            altHint={hasAlt}
                            twin={twinCase ? cells[twinCase]?.[selected.number] : undefined}
                            onPickTwin={twinCase ? () => select(twinCase, selected.number) : undefined}
                        />
                    )}
                </div>
            </div>

            <div className='mt-6 grid gap-6 xl:grid-cols-2 xl:items-start'>
                <TwinNote rows={cells} onPick={(c, n) => select(c as PronounCase, n)} />
                <PrathamaSummary mode={mode} />
            </div>

            <LabNav current='/grammar/sarvanam' />
            <div className='h-16' aria-hidden='true' />
        </div>
    )
}
